import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createAction,
} from "@reduxjs/toolkit";
import {
  BOHEquipment,
  CompositeCurveCalculationOutput,
  CompostiteCurveGraphData,
  CompPumpInputCalculated,
  Equipment,
  PumpCalcModel,
  PumpInput,
  PumpInputCalculated,
  PumpIteration,
  PumpModel,
  PumpPressureShaft,
} from "../../../../src/store/model/calculations/calculation";
import * as pumpService from "../../../services/calculations/calculation-output/pump.service";
import { ESP_ENDPOINTS } from "../../../services/calculations/endpoints";

interface State {
  loading: boolean;
  error: string | null;
  PumpInput: PumpInput;
  PumpInputCalculated: PumpInputCalculated;
  CompPumpInputCalculated: CompPumpInputCalculated;
  PumpModelE1: PumpModel;
  PumpList: PumpCalcModel[];
  PumpIteration: PumpIteration[];
  PumpPressureShaft: PumpPressureShaft;
  Chart1Data: any[];
  Chart2Data: any[];
  Chart3Data: any[];
  CompositeCurveData: any;
  pmpChrtPrdRt: string;
  pmpChrtPrdRtUnit: string;
  PumpCalculationComplete: boolean;
  GKXPumpIteration: PumpIteration[];
  ChargePump1Iteration: PumpIteration[];
  ChargePump2Iteration: PumpIteration[];
  ChargePump3Iteration: PumpIteration[];
  pumpFreeGasWarning: any;
  Equipments: Equipment[];
  ESPEquipments: any;
  ESPEquipmentsBOH: Equipment[];
  FilteredEquipments: Equipment[];
  BOHEquipments: any[];
  SelectedPump: PumpCalcModel | null;
  getSelectedPump: PumpCalcModel | null;
  maxEquipmentLimit: number;
  pumpLables: any;
  chartHeaders: any;
  FetchShaftRating: any;
  FetchShaftPercentageLoad: any;
  FetchPercentageHousingLoad: any;
}

export const initialState: State = {
  loading: false,
  error: null,
  PumpInput: {} as PumpInput,
  PumpInputCalculated: {} as PumpInputCalculated,
  CompPumpInputCalculated: {
    compositeFlowCorrectionFactor: 100,
    compositeHeadCorrectionFactor: 100,
    compositePowerCorrectionFactor: 100,
  },
  PumpModelE1: {} as PumpModel,
  PumpList: [],
  PumpIteration: [],
  PumpPressureShaft: {
    HousingPressureLimit: "0",
    Pmax: "0",
  },
  Chart1Data: [],
  Chart2Data: [],
  Chart3Data: [],
  CompositeCurveData: null,
  pmpChrtPrdRt: "",
  pmpChrtPrdRtUnit: "",
  PumpCalculationComplete: false,
  GKXPumpIteration: [],
  ChargePump1Iteration: [],
  ChargePump2Iteration: [],
  ChargePump3Iteration: [],
  Equipments: [],
  ESPEquipments: [],
  FilteredEquipments: [],
  ESPEquipmentsBOH: [],
  BOHEquipments: [],
  SelectedPump: null,
  getSelectedPump: null,
  pumpFreeGasWarning: undefined,
  maxEquipmentLimit: 0,
  chartHeaders: null,
  FetchShaftRating: null,
  pumpLables: undefined,
  FetchShaftPercentageLoad: null,
  FetchPercentageHousingLoad: null,
};

export const fetchBOHEquipments = createAction("pump/fetchBOHEquipments");
export const fetchSelectedPump = createAction("pump/fetchSelectedPump");
export const loadPumpHiddenShaftType = createAction("pump/loadPumpHiddenShaftType");

export const setFetchEquipments = createAction("pump/fetchEquipments");
export const fetchPumpData = createAsyncThunk(
  "pump/fetchPumpData",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await fetch(`${ESP_ENDPOINTS?.GET_PUMP_DATA}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return (await response.json()) as any;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadPumpList = createAsyncThunk(
  "pump/loadPumpList",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.loadPumpData(postData);
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const getPumpFreeGasWarning = createAsyncThunk(
  "pump/getPumpFreeGasWarning",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.loadPmpFreeGasCheck(postData);
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const loadPumpLables = createAsyncThunk(
  "pump/loadPumpLables",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.loadPumpCalcOutputLables(postData);
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const loadPumpLablesOnCorrectionFactorsOverride = createAsyncThunk(
  "pump/loadPumpLablesOnCorrectionFactorsOverride",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data =
        await pumpService.loadPumpCalcOutputForCorrectionFactorOverride(
          postData
        );
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const loadPumpIterations = createAsyncThunk(
  "pump/loadPumpIterations",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.loadPumpIteration(postData);
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const loadPumpPressureShaft = createAsyncThunk(
  "pump/loadPumpPressureShaft",
  async (
    {
      pmpId,
      noOfStgs,
      liqPhseDens,
      housingType,
      liqPhseDensUnit,
      unitSys,
    }: any,
    { rejectWithValue }
  ) => {
    try {
      const data = await pumpService.loadPumpMaxCalc(
        pmpId,
        noOfStgs,
        liqPhseDens,
        housingType,
        liqPhseDensUnit,
        unitSys
      );
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

export const loadChartHeaders = createAsyncThunk(
  "pump/loadChartHeaders",
  async (postData: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.converToOutUnit(postData);
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

// export const loadChart1Data = createAsyncThunk(
//     'pump/loadChart1Data',
//     async (postData: any, { rejectWithValue }) => {
//         try {
//             const data = await pumpService.LoadPlot1Graph(postData);
//             return data;
//         } catch (error) {
//             throw Error("Error fetching loadPumpList " + error);
//         }
//     }
// );

// export const loadCompositeChart1Data = createAsyncThunk(
//   "pump/loadCompositeChart1Data",
//   async (postData: any, { rejectWithValue }) => {
//     try {
//       const data = await pumpService.LoadCompositeGraph(postData);
//       return data;
//     } catch (error) {
//       throw Error("Error fetching loadPumpList " + error);
//     }
//   }
// );

// export const loadChart2Data = createAsyncThunk(
//     'pump/loadChart2Data',
//     async (postData: any, { rejectWithValue }) => {
//         try {
//             const data = await pumpService.LoadPlot2Graph(postData);
//             return data;
//         } catch (error) {
//             throw Error("Error fetching loadPumpList " + error);
//         }
//     }
// );

// export const loadChart3Data = createAsyncThunk(
//     'pump/loadChart3Data',
//     async (postData: any, { rejectWithValue }) => {
//         try {
//             const data = await pumpService.LoadPlot3Graph(postData);
//             return data;
//         } catch (error) {
//             throw Error("Error fetching loadPumpList " + error);
//         }
//     }
// );

export const fetchEquipments = createAsyncThunk(
  "pump/fetchEquipments",
  async ({ pumpName, equipmentType }: any, { rejectWithValue }) => {
    try {
      const data = await pumpService.fetchEquipmentsForPumps(
        pumpName,
        equipmentType
      );
      return data;
    } catch (error) {
      throw Error("Error fetching loadPumpList " + error);
    }
  }
);

// export const fetchBOHEquipments = createAsyncThunk(
//   "pump/fetchBOHEquipments",
//   async ({ pumpName, tubingOD }: any, { rejectWithValue }) => {
//     try {
//       const data = await pumpService.fetchBOHEquipments(pumpName, tubingOD);
//       return data;
//     } catch (error) {
//       throw Error("Error fetching loadPumpList " + error);
//     }
//   }
// );

const pumpSlice = createSlice({
  name: "pump",
  initialState,
  reducers: {
    resetPumpOutput: () => initialState,

    resetPumpFields: (state) => {
      state.PumpIteration = [];
    },

    setPumpInputDetails: (state, action) => {
      state.PumpInput = {
        ...state.PumpInput,
        pump_FullLoadSp:
          (action.payload.pump_FullLoadSp == 0 || action.payload.pump_FullLoadSp === null || !action.payload.pump_FullLoadSp)
            ? 3500
            : action.payload.pump_FullLoadSp,
        ...action?.payload
      };
    },

    setPumpPressureShaft: (state, action) => {
      state.PumpPressureShaft = action.payload;
    },
    setFetchShaftPercentageLoad: (state, action: PayloadAction<any>) => {
      state.FetchShaftPercentageLoad = action.payload;
    },
    setPumpInputCalculated: (state, action: PayloadAction<any>) => {
      state.PumpInputCalculated = action.payload;
    },

    setPumpFreeGasWarning: (state, action: PayloadAction<any>) => {
      if (state.PumpInputCalculated) {
        state.PumpInputCalculated.IPumpFreeGasWarningVisible = action.payload;
      }
    },

    setPumpFinalTDH: (state, action: PayloadAction<any>) => {
      if (state.PumpInputCalculated) {
        state.PumpInputCalculated.pmpFinalTDH = action.payload;
      }
    },

    setPumpPower: (state, action: PayloadAction<any>) => {
      if (state.PumpInputCalculated) {
        state.PumpInputCalculated.hPOPPump = action.payload;
      }
    },

    setPumpChart1Data: (state, action: PayloadAction<any[]>) => {
      state.Chart1Data = action.payload;
    },

    setCompositePumpCalculationOutput: (state, action: PayloadAction<any>) => {
      state.CompPumpInputCalculated = {
        ...state.CompPumpInputCalculated,
        ...action.payload,
      };
    },

    setCompositePumpChartData: (state, action) => {
      state.CompositeCurveData = action.payload;
    },

    setPumpChart2Data: (state, action: PayloadAction<any[]>) => {
      state.Chart2Data = action.payload;
    },

    setPumpChart3Data: (state, action: PayloadAction<any[]>) => {
      state.Chart3Data = action.payload;
    },

    setPumpChart1Header: (
      state,
      action: PayloadAction<{ pmpChrtPrdRt: string; pmpChrtPrdRtUnit: string }>
    ) => {
      state.pmpChrtPrdRt = action.payload.pmpChrtPrdRt;
      state.pmpChrtPrdRtUnit = action.payload.pmpChrtPrdRtUnit;
    },

    setPumpRequiredTDH: (state, action: PayloadAction<number>) => {
      if (state.PumpInput) {
        state.PumpInput.pump_ReqdTDH = action.payload;
      }
    },

    setPumpCalculationComplete: (state, action: PayloadAction<boolean>) => {
      state.PumpCalculationComplete = action.payload;
    },

    setPumpModelE1: (state, action: PayloadAction<any>) => {
      state.PumpModelE1 = action.payload;
    },

    setPumpList: (state, action: PayloadAction<any[]>) => {
      state.PumpList = action.payload;
    },

    setSelectedPump: (state, action: PayloadAction<any>) => {
      state.SelectedPump = action.payload;
    },
    setFetchShaftRating: (state, action: PayloadAction<any>) => {
      state.FetchShaftRating = action.payload;
    },
    setFetchPercentageHousingLoad: (state, action: PayloadAction<any>) => {
      state.FetchPercentageHousingLoad = action.payload;
    },
    // setGetSelectedPump: (state, action: PayloadAction<any>) => {
    //   state.SelectedPump = action.payload;
    // },

    setPumpIteration: (state, action: PayloadAction<any[]>) => {
      state.PumpIteration = action.payload;
    },

    setGKXPumpIteration: (state, action: PayloadAction<any[]>) => {
      state.GKXPumpIteration = action.payload;
    },

    setChargePump1Iteration: (state, action: PayloadAction<any[]>) => {
      state.ChargePump1Iteration = action.payload;
    },

    setChargePump2Iteration: (state, action: PayloadAction<any[]>) => {
      state.ChargePump2Iteration = action.payload;
    },

    setChargePump3Iteration: (state, action: PayloadAction<any[]>) => {
      state.ChargePump3Iteration = action.payload;
    },

    setPumpOverrideCorrectionFactors: (
      state,
      action: PayloadAction<{
        flowCorrection: number;
        headCorrection: number;
        powerCorrection: number;
      }>
    ) => {
      if (state.PumpInputCalculated) {
        state.PumpInputCalculated.flwCrrctnForPump =
          action.payload.flowCorrection.toString();
        state.PumpInputCalculated.hdCrrctnForPump =
          action.payload.headCorrection.toString();
        state.PumpInputCalculated.pWRCrrctnForPump =
          action.payload.powerCorrection.toString();
      }
    },

    setPumpCompositeOverrideCorrectionFactors: (
      state,
      action: PayloadAction<{
        flowCorrection: number;
        headCorrection: number;
        powerCorrection: number;
      }>
    ) => {
      state.CompPumpInputCalculated = {
        compositeFlowCorrectionFactor: action.payload.flowCorrection,
        compositeHeadCorrectionFactor: action.payload.headCorrection,
        compositePowerCorrectionFactor: action.payload.powerCorrection,
      };
    },

    setEquipmentsForPumps: (state, action: PayloadAction<any[]>) => {
      state.Equipments = action.payload.map((equipment) => ({
        ...equipment,
        text: equipment?.pN_Description,
        diameter: equipment?.od?.toString(),
        pn: equipment?.partNumber,
      }));
    },

    setESPEquipmentsForPumps: (state, action) => {
      state.ESPEquipments = action.payload;
    },
    setFilteredEquipments: (state, action) => {
      state.FilteredEquipments = action.payload;
    },

    setESPEquipmentsForBOH: (state, action) => {
      state.ESPEquipmentsBOH = action.payload;
    },

    setBOHEquipments: (state, action: PayloadAction<any[]>) => {
      state.BOHEquipments = action.payload.map((equipment) => ({
        ...equipment,
        text: equipment.boh,
        diameter: equipment?.od?.toString(),
      }));
    },

    setPumpDesiredStage: (state, action: PayloadAction<number>) => {
      if (state.PumpInput) {
        state.PumpInput.pump_DesrdStges = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPumpData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPumpData.fulfilled, (state, action) => {
        state.loading = false;
        state.PumpList = action.payload;
      })
      .addCase(fetchPumpData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loadPumpList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPumpList.fulfilled, (state, action) => {
        state.loading = false;
        state.PumpList = action.payload;
      })
      .addCase(loadPumpList.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(getPumpFreeGasWarning.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPumpFreeGasWarning.fulfilled, (state, action) => {
        state.loading = false;
        state.pumpFreeGasWarning = action.payload;
      })
      .addCase(getPumpFreeGasWarning.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(loadPumpLables.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPumpLables.fulfilled, (state, action) => {
        state.loading = false;
        state.pumpLables = action.payload;
      })
      .addCase(loadPumpLables.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(loadPumpLablesOnCorrectionFactorsOverride.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loadPumpLablesOnCorrectionFactorsOverride.fulfilled,
        (state, action) => {
          state.loading = false;
          state.pumpLables = action.payload;
        }
      )
      .addCase(
        loadPumpLablesOnCorrectionFactorsOverride.rejected,
        (state, action) => {
          state.loading = false;
          state.error = "";
        }
      )
      .addCase(loadPumpIterations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPumpIterations.fulfilled, (state, action) => {
        state.loading = false;
        state.PumpIteration = action.payload;
      })
      .addCase(loadPumpIterations.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(loadPumpPressureShaft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPumpPressureShaft.fulfilled, (state, action) => {
        state.loading = false;
        state.PumpPressureShaft = action.payload;
      })
      .addCase(loadPumpPressureShaft.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      .addCase(loadChartHeaders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadChartHeaders.fulfilled, (state, action) => {
        state.loading = false;
        state.chartHeaders = action.payload;
      })
      .addCase(loadChartHeaders.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      })
      //todo
      // .addCase(loadChart1Data.pending, (state) => {
      //     state.loading = true;
      //     state.error = null;
      // })
      // .addCase(loadChart1Data.fulfilled, (state, action) => {
      //     state.loading = false;
      //     state.Chart1Data = action.payload;
      // })
      // .addCase(loadChart1Data.rejected, (state, action) => {
      //     state.loading = false;
      //     state.error = "";
      // })
      // .addCase(loadCompositeChart1Data.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      // .addCase(loadCompositeChart1Data.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.CompositeCurveData = action.payload;
      // })
      // .addCase(loadCompositeChart1Data.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = "";
      // })
      // //todo
      // .addCase(loadChart2Data.pending, (state) => {
      //     state.loading = true;
      //     state.error = null;
      // })
      // .addCase(loadChart2Data.fulfilled, (state, action) => {
      //     state.loading = false;
      //     state.Chart2Data = action.payload;
      // })
      // .addCase(loadChart2Data.rejected, (state, action) => {
      //     state.loading = false;
      //     state.error = "";
      // })
      // .addCase(loadChart3Data.pending, (state) => {nvert to
      //     state.loading = true;
      //     state.error = null;
      // }) alUMP
      // .addCase(loadChart3Data.fulfilled, (state, action) => {
      //     state.loading = false;
      //     state.Chart3Data = action.payload;
      // })
      // .addCase(loadChart3Data.rejected, (state, action) => {gular
      //     state.loading = false;
      //     state.error = "";
      // })
      .addCase(fetchEquipments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEquipments.fulfilled, (state, action) => {
        state.loading = false;
        // state.Equipments = action.payload;
      })
      .addCase(fetchEquipments.rejected, (state, action) => {
        state.loading = false;
        state.error = "";
      });
    // .addCase(fetchBOHEquipments.pending, (state) => {
    //   state.loading = true;
    //   state.error = null;
    // })
    // .addCase(fetchBOHEquipments.fulfilled, (state, action) => {
    //   state.loading = false;
    //   // state.BOHEquipments = action.payload;
    // })
    // .addCase(fetchBOHEquipments.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = "";
    // });
  },
});

export const {
  resetPumpOutput,
  resetPumpFields,
  setPumpInputDetails,
  setPumpPressureShaft,
  setPumpInputCalculated,
  setPumpFreeGasWarning,
  setPumpChart1Data,
  setCompositePumpCalculationOutput,
  setCompositePumpChartData,
  setPumpChart2Data,
  setPumpChart3Data,
  setPumpChart1Header,
  setPumpRequiredTDH,
  setPumpCalculationComplete,
  setPumpModelE1,
  setPumpList,
  setSelectedPump,
  setPumpIteration,
  setGKXPumpIteration,
  setChargePump1Iteration,
  setChargePump2Iteration,
  setChargePump3Iteration,
  setPumpOverrideCorrectionFactors,
  setPumpCompositeOverrideCorrectionFactors,
  setEquipmentsForPumps,
  setFilteredEquipments,
  setESPEquipmentsForPumps,
  setESPEquipmentsForBOH,
  setBOHEquipments,
  setPumpDesiredStage,
  setFetchShaftRating,
  setFetchShaftPercentageLoad,
  setFetchPercentageHousingLoad,
  setPumpFinalTDH,
  setPumpPower
} = pumpSlice.actions;

export default pumpSlice.reducer;
